import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';

/**
 * Recently Viewed Products Component
 * Displays a horizontal scrollable list of recently viewed products
 */
const RecentlyViewed = ({ products, currentProductId }) => {
  // Filter out current product and limit display
  const displayProducts = products
    .filter(p => p.id !== currentProductId)
    .slice(0, 6);

  if (displayProducts.length === 0) {
    return null;
  }

  // Get the correct detail page URL based on section
  const getProductUrl = (product) => {
    switch (product.section) {
      case 'swag':
        return `/Swag/product/${product.id}`;
      case 'awards':
        return `/Awards/product/${product.id}`;
      case 'large-format':
        return `/LargeFormat/product/${product.id}`;
      default:
        return `/product/${product.id}`;
    }
  };

  // Get section display name
  const getSectionLabel = (section) => {
    switch (section) {
      case 'swag':
        return 'Promo Swag';
      case 'awards':
        return 'Awards';
      case 'large-format':
        return 'Large Format';
      default:
        return 'Small Format';
    }
  };

  // Format price display
  const formatPrice = (product) => {
    if (product.priceMax && product.priceMax !== product.price) {
      return `$${product.price.toFixed(2)} - $${product.priceMax.toFixed(2)}`;
    }
    return `$${product.price.toFixed(2)}`;
  };

  return (
    <section className="mt-16 pt-12 border-t border-ivory-300/50" data-testid="recently-viewed-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-gold-500/10 flex items-center justify-center">
          <Clock className="w-5 h-5 text-gold-600" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-semibold text-ivory-800">
            Recently Viewed
          </h2>
          <p className="text-sm text-ivory-500 font-body">
            Continue where you left off
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {displayProducts.map((product) => (
          <Link
            key={product.id}
            to={getProductUrl(product)}
            className="group"
            data-testid={`recently-viewed-${product.id}`}
          >
            <div className="bg-ivory-50 rounded-xl border border-ivory-300/50 overflow-hidden card-luxury h-full flex flex-col">
              {/* Product Image */}
              <div className="aspect-square overflow-hidden bg-ivory-100">
                <img
                  src={product.image || 'https://placehold.co/200x200/f5f3f0/9a8b7d?text=Product'}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.target.src = 'https://placehold.co/200x200/f5f3f0/9a8b7d?text=Product'; }}
                />
              </div>

              {/* Product Info */}
              <div className="p-3 flex-1 flex flex-col">
                {/* Section Badge */}
                <span className="text-[10px] uppercase tracking-wider text-gold-600 font-medium mb-1 font-body">
                  {getSectionLabel(product.section)}
                </span>

                {/* Product Name */}
                <h3 className="text-sm font-medium text-ivory-800 line-clamp-2 group-hover:text-gold-700 transition-colors mb-2 flex-1 font-body">
                  {product.name}
                </h3>

                {/* Price */}
                <div className="text-sm font-bold text-gold-600 font-body">
                  {formatPrice(product)}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* View All Link (if more products available) */}
      {products.length > 6 && (
        <div className="mt-4 text-center">
          <span className="text-sm text-ivory-500 font-body">
            +{products.length - 6} more recently viewed
          </span>
        </div>
      )}
    </section>
  );
};

export default RecentlyViewed;
